/**
 * Org-scoped production schedule filtering.
 *
 * Suppliers only see shop-schedule rows for deals where their org has an
 * approved/awarded quote or is on a hub task's `supplierOrgIds`. Staff preview
 * uses this client-side (staff already
 * has full data); supplier accounts get the same scoping server-side from the
 * `supplierProductionSchedule` callable.
 */
import type {
  OnboardingItem,
  ProjectTask,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  TaskGroup,
  TimberProject,
} from '../types';
import { buildVendorPortalDealGroups } from './vendorPortalDealGroups';
import { workGroupHasApprovedQuote } from './vendorPortalApprovals';
import { mirrorAllOnboardingTasks } from './onboardingTaskBridge';
import { projectHasSilentQaViewer } from './qaProjectAccess';

export function awardedDealIdsForSupplierOrg(
  orgId: string | undefined,
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): Set<string> {
  const out = new Set<string>();
  if (!orgId) return out;
  for (const group of buildVendorPortalDealGroups(orgId, rfqProjects, rfqPackages)) {
    const awarded = [group.timber, group.cnc].some(
      work => !!work && workGroupHasApprovedQuote(work, submissionsByPackage, orgId),
    );
    if (awarded) out.add(group.dealId);
  }
  return out;
}

/** Deals where this org is on `supplierOrgIds` for a hub task or onboarding row. */
export function dealIdsWithHubTaskSupplierOrg(input: {
  orgId: string | undefined;
  timberProjects: TimberProject[];
  projectTasks: ProjectTask[];
  projectOnboardings: Array<{ projectId?: string; items?: OnboardingItem[] }>;
}): Set<string> {
  const out = new Set<string>();
  const orgId = input.orgId?.trim();
  if (!orgId) return out;
  const dealByProjectId = new Map(
    input.timberProjects.filter((p) => p.id && p.dealId).map((p) => [p.id!, p.dealId!.trim()]),
  );
  for (const t of input.projectTasks) {
    if (!(t.supplierOrgIds ?? []).includes(orgId)) continue;
    const dealId = dealByProjectId.get(t.projectId);
    if (dealId) out.add(dealId);
  }
  for (const ob of input.projectOnboardings) {
    if (!ob.projectId) continue;
    const dealId = dealByProjectId.get(ob.projectId);
    if (!dealId) continue;
    const hit = (ob.items ?? []).some((item) => (item.supplierOrgIds ?? []).includes(orgId));
    if (hit) out.add(dealId);
  }
  return out;
}

export function scopeProductionScheduleToSupplier(params: {
  orgId: string | undefined;
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  submissionsByPackage: Map<string, RfqSubmission[]>;
  timberProjects: TimberProject[];
  projectTasks: ProjectTask[];
  taskGroups?: TaskGroup[];
  /**
   * Onboarding checklists for the hub projects. The internal hub mirrors these
   * into virtual tasks (`onboarding-task:` ids) that anchor groups like
   * Production; real production tasks are often subtasks of those virtual
   * parents, so the supplier timeline needs the same mirroring or the group
   * renders empty.
   */
  projectOnboardings?: Array<{ projectId?: string; items?: OnboardingItem[] }>;
  /** Test-supplier uid — include hub projects on the silent QA list even if the org is not awarded. */
  qaViewerUid?: string;
}): { projects: TimberProject[]; tasks: ProjectTask[]; groups: TaskGroup[] } {
  const {
    orgId,
    rfqProjects,
    rfqPackages,
    submissionsByPackage,
    timberProjects,
    projectTasks,
    taskGroups = [],
    projectOnboardings = [],
    qaViewerUid,
  } = params;
  const dealIds = awardedDealIdsForSupplierOrg(
    orgId,
    rfqProjects,
    rfqPackages,
    submissionsByPackage,
  );
  Array.from(
    dealIdsWithHubTaskSupplierOrg({
      orgId,
      timberProjects,
      projectTasks,
      projectOnboardings,
    }),
  ).forEach((id) => {
    dealIds.add(id);
  });
  const projects = timberProjects.filter(
    p =>
      (!!p.dealId && dealIds.has(p.dealId))
      || projectHasSilentQaViewer(p, qaViewerUid),
  );
  const projectIds = new Set(projects.map(p => p.id).filter(Boolean));
  const tasks = projectTasks.filter(t => projectIds.has(t.projectId));
  const groups = taskGroups.filter(g => projectIds.has(g.projectId));
  const mirrored = mirrorAllOnboardingTasks(
    projectOnboardings.filter(ob => !!ob.projectId && projectIds.has(ob.projectId)),
    groups,
  );
  return { projects, tasks: [...mirrored, ...tasks], groups };
}
